import classNames from "classnames";
import { type HTMLAttributes, type ReactNode } from "react";

import Dot from "../helpers/Dot";

export type BadgeProps = {
  startIcon?: ReactNode | "dots";
  endIcon?: ReactNode | "dots";
  color?: keyof typeof colors;
  variant?: "pillColor" | "pillOutline" | "badgeColor" | "modern";
  rounded?: keyof typeof roundedes;
  size?: keyof typeof sizes;
} & HTMLAttributes<HTMLSpanElement>;

const roundedes = {
  md: "rounded-md",
  lg: "rounded-lg",
  "2xl": "rounded-2xl",
};
const colors = {
  primary: {
    pillColor: "bg-primary-50 text-primary-700 border border-primary-200",
    pillOutline: "border-primary-600 border-1.5 text-primary-700",
    badgeColor: "bg-primary-50 text-primary-700 border border-primary-200",
    modern: "",
  },
  success: {
    pillColor: "bg-success-50 text-success-700 border border-success-200",
    pillOutline: "border-success-600 border-1.5 text-success-700",
    badgeColor: "bg-success-50 text-success-700 border border-success-200",
    modern: "",
  },
  error: {
    pillColor: "bg-error-50 text-error-700 border border-error-200",
    pillOutline: "border-error-600 border-1.5 text-error-700",
    badgeColor: "bg-error-50 text-error-700 border border-error-200",
    modern: "",
  },
  warning: {
    pillColor: "bg-warning-50 text-warning-700 border border-warning-200",
    pillOutline: "border-warning-600 border-1.5 text-warning-700",
    badgeColor: "bg-warning-50 text-warning-700 border border-warning-200",
    modern: "",
  },
  gray: {
    pillColor: "bg-gray-100 text-gray-700 border border-gray-200",
    pillOutline: "border-gray-600 border-1.5 text-gray-700",
    badgeColor: "bg-gray-100 text-gray-700 border border-gray-200",
    modern: "",
  },
  grayBlue: {
    pillColor: "bg-grayblue-100 text-grayblue-700 border border-grayblue-200",
    pillOutline: "border-grayblue-600 border-1.5 text-grayblue-700",
    badgeColor: "bg-grayblue-100 text-grayblue-700 border border-grayblue-200",
    modern: "",
  },
  blueLight: {
    pillColor:
      "bg-bluelight-100 text-bluelight-700 border border-bluelight-200",
    pillOutline: "border-bluelight-600 border-1.5 text-bluelight-700",
    badgeColor:
      "bg-bluelight-100 text-bluelight-700 border border-bluelight-200",
    modern: "",
  },
  blue: {
    pillColor: "bg-blue-100 text-blue-700 border border-blue-200",
    pillOutline: "border-blue-600 border-1.5 text-blue-700",
    badgeColor: "bg-blue-100 text-blue-700 border border-blue-200",
    modern: "",
  },
  blueDark: {
    pillColor: "bg-bluedark-50 text-bluedark-700 border border-bluedark-200",
    pillOutline: "border-bluedark-600 border-1.5 text-bluedark-700",
    badgeColor: "bg-bluedark-50 text-bluedark-700 border border-bluedark-200",
    modern: "",
  },
  indigo: {
    pillColor: "bg-indigo-100 text-indigo-700 border border-indigo-200",
    pillOutline: "border-indigo-600 border-1.5 text-indigo-700",
    badgeColor: "bg-indigo-100 text-indigo-700 border border-indigo-200",
    modern: "",
  },
  purple: {
    pillColor: "bg-purple-100 text-purple-700 border border-purple-200",
    pillOutline: "border-purple-600 border-1.5 text-purple-700",
    badgeColor: "bg-purple-100 text-purple-700 border border-purple-200",
    modern: "",
  },
  pink: {
    pillColor: "bg-pink-100 text-pink-700 border border-pink-200",
    pillOutline: "border-pink-600 border-1.5 text-pink-700",
    badgeColor: "bg-pink-100 text-pink-700 border border-pink-200",
    modern: "",
  },
  orange: {
    pillColor: "bg-orange-100 text-orange-700 border border-orange-200",
    pillOutline: "border-orange-600 border-1.5 text-orange-700",
    badgeColor: "bg-orange-100 text-orange-700 border border-orange-200",
    modern: "",
  },
};

const modernStyle = "bg-white text-gray-700 border border-gray-300 shadow-xs";

const sizes = {
  sm: "py-[3px] ps-[6px] pe-[8px] text-xs font-medium gap-[3px]",
  md: "py-0.5 ps-[7px] pe-[9px] text-sm font-medium leading-[18px] gap-[3px]",
  lg: "py-1 ps-[9px] pe-2.5 text-sm font-medium leading-[18px] gap-[3px]",
};

const Badge = function Badge({
  startIcon,
  endIcon,
  children,
  color = "primary",
  variant = "pillColor",
  size = "md",
  rounded = "2xl",
  className,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={classNames(
        "inline-flex items-center",
        className,
        variant === "modern" ? modernStyle : colors[color][variant],
        roundedes[rounded],
        sizes[size],
      )}
      {...rest}
    >
      {startIcon && (
        <span className="-ms-0.5">
          {startIcon === "dots" ? <Dot color={color} /> : startIcon}
        </span>
      )}
      <span className="inline-flex min-w-0 items-center gap-1.5">
        {children}
      </span>
      {endIcon && (
        <span className="-me-1">
          {endIcon === "dots" ? <Dot color={color} /> : endIcon}
        </span>
      )}
    </span>
  );
};

export default Badge;
